import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';

export interface MeetingBidDocumentsDialogData {
  bidId: string;
  vendorName?: string;
}

interface SupportingDocument {
  attachmentId?: string;
  attachmentFor?: string;
  name?: string;
  fileContent?: string;
  mimeType?: string;
  fileSize?: number;
  documentExpiryDate?: string;
}

@Component({
  selector: 'app-bid-documents-dialog',
  templateUrl: './bid-documents-dialog.component.html',
  styleUrls: ['./bid-documents-dialog.component.scss']
})
export class BidDocumentsDialogComponent implements OnInit {
  documents: SupportingDocument[] = [];
  documentGroups: Array<{ category: string; documents: SupportingDocument[] }> = [];
  isLoading = true;

  constructor(
    private dialogRef: MatDialogRef<BidDocumentsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MeetingBidDocumentsDialogData,
    private apiService: ApiService,
    private toasterService: ToasterService
  ) {}

  ngOnInit(): void {
    this.loadDocuments();
  }

  private groupDocuments(): void {
    const groups = new Map<string, SupportingDocument[]>();
    this.documents.forEach(document => {
      const category = document.attachmentFor || 'Other documents';
      groups.set(category, [...(groups.get(category) || []), document]);
    });
    this.documentGroups = Array.from(groups.entries()).map(([category, documents]) => ({ category, documents }));
  }

  close(): void {
    this.dialogRef.close();
  }

  viewDocument(attachment: SupportingDocument): void {
    if (!attachment.fileContent) return;
    window.open(attachment.fileContent, '_blank', 'noopener,noreferrer');
  }

  async downloadDocument(attachment: SupportingDocument): Promise<void> {
    if (!attachment.fileContent) return;
    try {
      const response = await fetch(attachment.fileContent);
      if (!response.ok) throw new Error('Unable to retrieve the document.');
      const blob = await response.blob();
      const fileUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = attachment.name || 'document';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(fileUrl);
    } catch (error) {
      console.error('Unable to download bid document.', error);
      this.toasterService.showError('Unable to download the document.', 'Document Error');
    }
  }

  formatFileSize(fileSize?: number): string {
    if (!fileSize) return '';
    return fileSize < 1024 ? `${fileSize} B` : `${(fileSize / 1024).toFixed(2)} KB`;
  }

  private async loadDocuments(): Promise<void> {
    try {
      const response: any = await lastValueFrom(this.apiService.getByUrl(
        `${environment.API_URL}/api/Meetings/bids/${encodeURIComponent(this.data.bidId)}/supporting-documents`
      ));
      if (!response?.isSuccess) throw new Error(response?.errorMessageEn || 'Unable to load bid documents.');
      this.documents = Array.isArray(response?.data) ? response.data : [];
      this.groupDocuments();
    } catch (error: any) {
      this.documents = [];
      this.documentGroups = [];
      console.error('Unable to load bid documents.', error);
    } finally {
      this.isLoading = false;
    }
  }
}
